<script setup lang="ts">
import { ref, computed } from "vue";

import { useDate } from "@shared/lib/useDate";
import AppButtonSmall from "@shared/ui/components/AppButtonSmall.vue";
import AppMenu from "@shared/ui/components/AppMenu.vue";
import AppMenuContent from "@shared/ui/components/AppMenuContent.vue";
import AppMenuItem from "@shared/ui/components/AppMenuItem.vue";
import AppModal from "@shared/ui/components/AppModal.vue";
import IconBookmark from "@shared/ui/icons/IconBookmark.vue";
import IconDelete from "@shared/ui/icons/IconDelete.vue";
import IconMore from "@shared/ui/icons/IconMore.vue";
import IconPhone from "@shared/ui/icons/IconPhone.vue";
import IconSearch from "@shared/ui/icons/IconSearch.vue";

import UserCard from "@entities/user/ui/UserCard.vue";

import ChatDeleteModal from "./ChatDeleteModal.vue";

import type { PeerInfo } from "shared";

const props = defineProps<{
    peer: PeerInfo;
    isOnline: boolean;
    lastSeen: number | null;
    verified: boolean | null;
    // Чат с собой: шапка «Избранное», без статуса/верификации/меню.
    isFavorites?: boolean;
}>();

const { formatLastSeen } = useDate();

const statusText = computed(() => {
    if (props.isOnline) {
        return "онлайн";
    }
    if (props.lastSeen) {
        return formatLastSeen(props.lastSeen);
    }
    return "офлайн";
});

const emit = defineEmits<{
    openSafety: [];
    deleteChat: [];
    deleteChatForAll: [];
}>();

// Поиск/звонки ещё не реализованы — по клику показываем заглушку с заголовком.
const stubOpen = ref(false);
const stubTitle = ref("");
function openStub(title: string): void {
    stubTitle.value = title;
    stubOpen.value = true;
}
// Подтверждение удаления чата (та же модалка, что у сообщений).
const deleteOpen = ref(false);
function onDeleteConfirm(alsoPeer: boolean): void {
    if (alsoPeer) {
        emit("deleteChatForAll");
    } else {
        emit("deleteChat");
    }
    deleteOpen.value = false;
}
</script>

<template>
    <div class="chat-header">
        <!-- Header row -->
        <div class="chat-header__row">
            <div v-if="isFavorites" class="chat-header__favorites">
                <IconBookmark
                    fill="currentColor"
                    class="chat-header__fav-ico"
                />
                <span class="chat-header__fav-title">Избранное</span>
            </div>
            <UserCard
                v-else
                :peer="peer"
                :online="isOnline"
                :show-avatar="false"
                :verified="verified"
                :on-click-verified="() => emit('openSafety')"
                variant="small"
            >
                <template #text>{{ statusText }}</template>
            </UserCard>

            <!-- Действия: поиск · звонок · ещё (меню удаления). В избранном звонка
                 нет, поиск и удаление — есть. -->
            <div class="chat-header__actions">
                <AppButtonSmall
                    v-if="!isFavorites"
                    :size="34"
                    title="Звонок"
                    @click="openStub('Звонок')"
                >
                    <IconPhone />
                </AppButtonSmall>

                <AppButtonSmall
                    :size="34"
                    title="Поиск"
                    @click="openStub('Поиск')"
                >
                    <IconSearch />
                </AppButtonSmall>

                <AppMenu placement="bottom-end">
                    <template #trigger="{ isOpen }">
                        <AppButtonSmall :size="34" title="Ещё" :active="isOpen">
                            <IconMore />
                        </AppButtonSmall>
                    </template>
                    <template #default="{ close }">
                        <AppMenuContent>
                            <AppMenuItem
                                variant="danger"
                                @click="
                                    deleteOpen = true;
                                    close();
                                "
                            >
                                <IconDelete />
                                {{
                                    isFavorites
                                        ? "Очистить избранное"
                                        : "Удалить чат"
                                }}
                            </AppMenuItem>
                        </AppMenuContent>
                    </template>
                </AppMenu>
            </div>
        </div>

        <AppModal
            :is-visible="stubOpen"
            :title="stubTitle"
            :max-width="340"
            @close="stubOpen = false"
        >
            <p class="chat-header__stub">Функция пока недоступна.</p>
        </AppModal>

        <ChatDeleteModal
            :visible="deleteOpen"
            :is-self="true"
            :is-favorites="isFavorites"
            :title="isFavorites ? 'Очистить избранное' : 'Удалить чат'"
            @confirm="onDeleteConfirm"
            @close="deleteOpen = false"
        />
    </div>
</template>

<style scoped lang="scss">
.chat-header {
    &__row {
        height: 60px;
        padding: 0 22px;
        border-bottom: 1px solid var(--mc-line);
        display: flex;
        align-items: center;
        gap: 14px;
    }

    &__favorites {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__fav-ico {
        width: 22px;
        height: 22px;
        color: var(--mc-acid);
    }

    &__fav-title {
        font-size: 15px;
        font-weight: 700;
        color: var(--mc-fg);
    }

    // Действия справа: поиск/звонок/ещё (AppButtonSmall = .mc-btn-sm).
    &__actions {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 4px;
    }

    &__actions :deep(.mc-btn-sm svg) {
        width: 20px;
        height: 20px;
    }

    &__stub {
        font-family: var(--mc-mono);
        font-size: 13px;
        color: var(--mc-fg-mute);
    }
}
</style>
